import React, { useState } from "react";
import { api } from "../../config/api";
import { useMyContext } from "../../context/MyContext";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { FaEye, FaEyeSlash } from "react-icons/fa";

const AdminAuthComponent = ({ onAuthenticated }) => {
    const { token, user } = useMyContext();
    const [password, setPassword] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showForgotPassword, setShowForgotPassword] = useState(false);
    const [email, setEmail] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const navigate = useNavigate();

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!password) {
            toast.error("Please enter your password");
            return;
        }

        setIsSubmitting(true);

        try {
            // Add user role to the request payload
            const response = await api.post(
                "/admin-auth/verify",
                {
                    password,
                    role: user?.role // Send the user's role to the backend
                },
                { headers: { authorization: `Bearer ${token}` } }
            );

            if (response.data.verified) {
                // Save authentication state to session storage
                sessionStorage.setItem("adminAuthenticated", "true");
                onAuthenticated(true);
                toast.success("Authentication successful");
            }
        } catch (error) {
            console.error("Authentication error:", error);

            // Handle specific error cases
            if (error.response?.status === 403) {
                toast.error("Only administrators can access this feature");
            } else {
                toast.error(error.response?.data?.error || "Authentication failed");
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleForgotPassword = async (e) => {
        e.preventDefault();

        if (!email) {
            toast.error("Please enter your email");
            return;
        }

        setIsSubmitting(true);

        try {
            const response = await api.post(
                "/admin-auth/forgot-password",
                {
                    email,
                    role: user?.role // Send the user's role to the backend
                },
                { headers: { authorization: `Bearer ${token}` } }
            );

            toast.success("Password reset email sent. Please check your inbox.");
            setShowForgotPassword(false);
        } catch (error) {
            console.error("Forgot password error:", error);

            if (error.response?.status === 403 && error.response?.data?.error?.includes("email is not authorized")) {
                toast.error("This email is not authorized to reset the password");
            } else {
                toast.error(error.response?.data?.error || "Failed to send reset email");
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    const toggleForgotPassword = () => {
        setShowForgotPassword(!showForgotPassword);
    };

    return (
        <div className="flex justify-center items-center min-h-[60vh]">
            <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full">
                <h2 className="text-2xl font-bold mb-6 text-center text-[#053F92]">
                    {showForgotPassword ? "Reset Admin Password" : "Admin Authentication"}
                </h2>

                {showForgotPassword ? (
                    <form onSubmit={handleForgotPassword} className="space-y-4">
                        <div>
                            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                                Email
                            </label>
                            <input
                                type="email"
                                id="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                placeholder="Enter your email"
                                required
                            />
                        </div>
                        <p className="text-sm text-gray-600">
                            Please enter your authorized email address. Only authorized administrators can reset the password.
                        </p>
                        <div className="flex justify-between items-center pt-2">
                            <button
                                type="button"
                                onClick={toggleForgotPassword}
                                className="text-sm text-[#053F92] hover:underline"
                            >
                                Back to previous
                            </button>

                            <button
                                type="submit"
                                disabled={isSubmitting}
                                className="px-4 py-2 bg-[#053F92] text-white rounded-md hover:bg-[#032d69] transition duration-300 disabled:opacity-50"
                            >
                                {isSubmitting ? "Sending..." : "Send Reset Link"}
                            </button>
                        </div>
                    </form>
                ) : (
                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div>
                            <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                                Password
                            </label>
                            <div className="relative">
                                <input
                                    type={showPassword ? "text" : "password"}
                                    id="password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    className="w-full pr-10 px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    placeholder="Enter admin password"
                                    required
                                />
                                <button
                                    type="button"
                                    aria-label={showPassword ? "Hide password" : "Show password"}
                                    onClick={() => setShowPassword((v) => !v)}
                                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-600 hover:text-gray-800"
                                >
                                    {showPassword ? <FaEye /> : <FaEyeSlash />}
                                </button>
                            </div>
                        </div>

                        <div className="flex justify-between items-center pt-2">
                            <button
                                type="button"
                                onClick={toggleForgotPassword}
                                className="text-sm text-[#053F92] hover:underline"
                            >
                                Forgot Password?
                            </button>

                            <button
                                type="submit"
                                disabled={isSubmitting}
                                className="px-4 py-2 bg-[#053F92] text-white rounded-md hover:bg-[#032d69] transition duration-300 disabled:opacity-50"
                            >
                                {isSubmitting ? "Authenticating..." : "View"}
                            </button>
                        </div>
                    </form>
                )}
            </div>
        </div>
    );
};

export default AdminAuthComponent;